import React from 'react';
import { Exercise } from '../../../../services/exerciseService';
import { PencilIcon, TrashIcon, MinusIcon, PlusIcon } from '@heroicons/react/24/solid';

interface TableRowProps {
    exercise: Exercise;
    expandedRow: number | null;
    onEdit: () => void;
    onDelete: () => void;
    onMore: () => void;
}

const getGenderLabels = (genders: string[] | undefined) => {
    const labels: Record<string, string> = {
        male: 'Male',
        female: 'Female'
    };
    return genders && genders.length > 0
        ? genders.map(gender => labels[gender] || gender).join(', ')
        : 'N/A';
};

const getFitnessGoalLabels = (goals: string[] | undefined) => {
    const labels: Record<string, string> = {
        'build-muscles': 'Build Muscles',
        'lose-weight': 'Lose Weight'
    };
    return goals && goals.length > 0
        ? goals.map(goal => labels[goal] || goal).join(', ')
        : 'N/A';
};

const getMoodLabels = (moods: string[] | undefined) => {
    const labels: Record<string, string> = {
        energetic: 'Energetic',
        normal: 'Normal',
        tired: 'Tired'
    };
    return moods && moods.length > 0
        ? moods.map(mood => labels[mood] || mood).join(', ')
        : 'N/A';
};

const getTargetMuscles = (exercise: Exercise) => {
    const primary = exercise.primaryTargetMuscles.map(m => m.name);
    const secondary = exercise.secondaryTargetMuscles.map(m => m.name);

    return [...primary, ...secondary].join(', ') || 'N/A';
};

const getFormattedFitnessLevels = (levels: string[] | undefined) => {
    if (!levels || levels.length === 0) return 'N/A';
    return levels
        .map(level => level.charAt(0).toUpperCase() + level.slice(1))
        .join(', ');
};

const TableRow: React.FC<TableRowProps> = ({
    exercise,
    expandedRow,
    onEdit,
    onDelete,
    onMore
}) => {
    const categoryNames = exercise.categories.map(c => c.title).join(', ');
    const programNames = exercise.programs.map(p => p.title).join(', ');
    const targetMuscles = getTargetMuscles(exercise);

    return (
        <>
            <tr className="border-t hover:bg-gray-50">
                <td className="py-3 px-4 text-sm">{exercise.id}</td>
                <td className="py-3 px-4 text-sm">{exercise.name}</td>
                <td className="py-3 px-4 text-sm">{categoryNames || 'N/A'}</td>
                <td className="py-3 px-4 text-sm">{programNames || 'N/A'}</td>
                <td className="py-3 px-4 text-sm">{getGenderLabels(exercise.gender)}</td>
                <td className="py-3 px-4 text-sm capitalize">{getFormattedFitnessLevels(exercise.fitnessLevel)}</td>
                <td className="py-3 px-4 text-sm capitalize">{getMoodLabels(exercise.mood)}</td>
                <td className="py-3 px-4 text-sm">{getFitnessGoalLabels(exercise.fitnessGoal)}</td>
                <td className="py-3 px-4 text-sm hidden lg:table-cell">{targetMuscles}</td>
                <td className="py-3 px-4">
                    <div className="flex gap-2">
                        <button
                            onClick={() => onMore()}
                            className="px-2 py-1 text-red-600 border border-red-600 rounded hover:bg-gray-100"
                        >
                            {expandedRow === exercise.id ? <MinusIcon className="w-5 h-5" /> : <PlusIcon className="w-5 h-5" />}
                        </button>
                        <button
                            onClick={() => onEdit()}
                            className="px-2 py-1 text-yellow-600 border border-yellow-600 rounded hover:bg-gray-100"
                        >
                            <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => onDelete()}
                            className="px-2 py-1 text-red-600 border border-red-600 rounded hover:bg-gray-100"
                        >
                            <TrashIcon className="w-5 h-5" />
                        </button>
                    </div>
                </td>
            </tr>
        </>
    );
};

export default TableRow;
